import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReviewSubmissionWidget from "../components/reviews/ReviewSubmissionWidget";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function OrderReview() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [submitted, setSubmitted] = useState({});

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await axios.get(`${API}/api/orders/${orderId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setOrder(res.data);
      // Auto-select first product if only one item
      const items = res.data?.line_items || [];
      if (items.length === 1) setSelectedProduct(items[0]);
    } catch (e) {
      // If order not found or auth fails, still let guest review by product ID from URL
      setError("Could not load order details. You can still leave a review below.");
    } finally {
      setLoading(false);
    }
  };

  const handleReviewDone = (productId) => {
    setSubmitted((prev) => ({ ...prev, [productId]: true }));
    setSelectedProduct(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900" />
      </div>
    );
  }

  const lineItems = order?.line_items || [];
  const reviewedAll = lineItems.length > 0 && lineItems.every((i) => submitted[i.product_id || i.id]);

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-gray-500 hover:text-gray-800 mb-4 flex items-center gap-1"
            data-testid="back-btn"
          >
            ← Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {reviewedAll ? "Thanks for your reviews!" : "Rate Your Purchase"}
          </h1>
          {order && (
            <p className="text-gray-500 text-sm mt-1">
              Order #{order.order_number || orderId}
            </p>
          )}
        </div>

        {/* All reviewed */}
        {reviewedAll && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
            <div className="text-4xl mb-3">⭐</div>
            <p className="text-lg font-semibold text-green-800">All reviews submitted!</p>
            <p className="text-green-700 text-sm mt-1">
              Your feedback helps other buyers and supports the vendor.
            </p>
            <button
              onClick={() => navigate("/")}
              className="mt-5 bg-gray-900 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-700 transition"
              data-testid="back-home-btn"
            >
              Back to Home
            </button>
          </div>
        )}

        {/* Product selector — shown if order loaded and not yet reviewed */}
        {!reviewedAll && lineItems.length > 0 && !selectedProduct && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 font-medium mb-2">
              Select a product to review:
            </p>
            {lineItems.map((item) => {
              const pid = item.product_id || item.id;
              const isReviewed = submitted[pid];
              return (
                <button
                  key={pid}
                  onClick={() => !isReviewed && setSelectedProduct(item)}
                  disabled={isReviewed}
                  data-testid={`product-select-${pid}`}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border text-left transition
                    ${isReviewed
                      ? "border-green-200 bg-green-50 opacity-60 cursor-default"
                      : "border-gray-200 bg-white hover:border-gray-400 hover:shadow-sm cursor-pointer"
                    }`}
                >
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-14 h-14 rounded-lg object-cover border border-gray-100 flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{item.title}</p>
                    {item.variant_title && (
                      <p className="text-xs text-gray-500">{item.variant_title}</p>
                    )}
                  </div>
                  {isReviewed ? (
                    <span className="text-green-600 text-sm font-medium flex-shrink-0">✓ Reviewed</span>
                  ) : (
                    <span className="text-gray-400 text-sm flex-shrink-0">Review →</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Fallback: no order — show generic form */}
        {!reviewedAll && lineItems.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <p className="text-sm text-gray-500 mb-4">
              {error || "Enter the product ID to leave your review."}
            </p>
            <ReviewSubmissionWidget
              orderId={orderId}
              onSuccess={() => setSubmitted({ fallback: true })}
            />
          </div>
        )}

        {/* Review form for selected product */}
        {selectedProduct && !reviewedAll && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-5">
              {selectedProduct.image && (
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.title}
                  className="w-12 h-12 rounded-lg object-cover border border-gray-100"
                />
              )}
              <div>
                <p className="font-semibold text-gray-900 text-sm">{selectedProduct.title}</p>
                {selectedProduct.variant_title && (
                  <p className="text-xs text-gray-500">{selectedProduct.variant_title}</p>
                )}
              </div>
              {lineItems.length > 1 && (
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="ml-auto text-xs text-gray-400 hover:text-gray-700"
                  data-testid="change-product-btn"
                >
                  Change
                </button>
              )}
            </div>

            <ReviewSubmissionWidget
              productId={selectedProduct.product_id || selectedProduct.id}
              orderId={orderId}
              customerName={order?.customer
                ? `${order.customer.first_name || ""} ${order.customer.last_name || ""}`.trim()
                : ""}
              onSuccess={() => handleReviewDone(selectedProduct.product_id || selectedProduct.id)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
